import { ClientApi } from './chunk-5ZVPCOO5.mjs';
import { PreviewWeb } from './chunk-E7EUR3MU.mjs';
import { addons } from './chunk-ZOAWZ2XS.mjs';
import { global } from '@storybook/global';
import { createBrowserChannel } from '@storybook/channels';
import { FORCE_RE_RENDER } from '@storybook/core-events';
import { logger } from '@storybook/client-logger';

function executeLoadable(loadable) {
  let reqs = null;
  if (Array.isArray(loadable)) {
    reqs = loadable;
  } else if (loadable.keys) {
    reqs = [loadable];
  }
  let exportsMap = /* @__PURE__ */ new Map();
  if (reqs) {
    reqs.forEach((req) => {
      req.keys().forEach((filename) => {
        try {
          const fileExports = req(filename);
          exportsMap.set(
            typeof req.resolve === "function" ? req.resolve(filename) : filename,
            fileExports
          );
        } catch (error) {
          const errorString = error.message && error.stack ? `${error.message}
 ${error.stack}` : error.toString();
          logger.error(`Unexpected error while loading ${filename}: ${errorString}`);
        }
      });
    });
  } else {
    const exported = loadable();
    if (Array.isArray(exported) && exported.every((obj) => obj.default != null)) {
      exportsMap = new Map(
        exported.map((fileExports, index) => [`exports-map-${index}`, fileExports])
      );
    } else if (exported) {
      logger.warn(
        `Loader function passed to 'configure' should return void or an array of module exports that all contain a 'default' export. Received: ${JSON.stringify(
          exported
        )}`
      );
    }
  }
  return exportsMap;
}
function executeLoadableForChanges(loadable, m) {
  let lastExportsMap = m?.hot?.data?.lastExportsMap || /* @__PURE__ */ new Map();
  if (m?.hot?.dispose) {
    m.hot.accept();
    m.hot.dispose((data) => {
      data.lastExportsMap = lastExportsMap;
    });
  }
  const exportsMap = executeLoadable(loadable);
  const added = /* @__PURE__ */ new Map();
  Array.from(exportsMap.entries()).filter(([, fileExports]) => !!fileExports.default).filter(([fileName, fileExports]) => lastExportsMap.get(fileName) !== fileExports).forEach(([fileName, fileExports]) => added.set(fileName, fileExports));
  const removed = /* @__PURE__ */ new Map();
  Array.from(lastExportsMap.keys()).filter((fileName) => !exportsMap.has(fileName)).forEach((fileName) => {
    const value = lastExportsMap.get(fileName);
    if (value) {
      removed.set(fileName, value);
    }
  });
  lastExportsMap = exportsMap;
  return { added, removed };
}

// src/modules/core-client/start.ts
var { FEATURES } = global;
var removedApi = (name) => () => {
  throw new Error(`@storybook/client-api:${name} was removed in storyStoreV7.`);
};
function start(renderToCanvas, { decorateStory, render } = {}) {
  if (global) {
    global.IS_STORYBOOK = true;
  }
  if (FEATURES?.storyStoreV7) {
    return {
      forceReRender: removedApi("forceReRender"),
      configure: removedApi("configure"),
      clientApi: {
        storiesOf: removedApi("clientApi.storiesOf"),
        raw: removedApi("raw")
      }
    };
  }
  const channel = createBrowserChannel({ page: "preview" });
  addons.setChannel(channel);
  const clientApi = global?.__STORYBOOK_CLIENT_API__ || new ClientApi();
  const preview = global?.__STORYBOOK_PREVIEW__ || new PreviewWeb();
  let initialized = false;
  const importFn = (path) => clientApi.importFn(path);
  function onStoriesChanged() {
    const storyIndex = clientApi.getStoryIndex();
    preview.onStoriesChanged({ storyIndex, importFn });
  }
  clientApi.onImportFnChanged = onStoriesChanged;
  clientApi.storyStore = preview.storyStore;
  if (global) {
    global.__STORYBOOK_CLIENT_API__ = clientApi;
    global.__STORYBOOK_ADDONS_CHANNEL__ = channel;
    global.__STORYBOOK_PREVIEW__ = preview;
    global.__STORYBOOK_STORY_STORE__ = preview.storyStore;
  }
  return {
    forceReRender: () => channel.emit(FORCE_RE_RENDER),
    clientApi,
    // This gets called each time the user calls configure (i.e. once per HMR)
    // The first time, it constructs the preview, subsequently it updates it
    configure(renderer, loadable, m, disableBackwardCompatibility = true) {
      if (disableBackwardCompatibility) {
        throw new Error("unexpected configure() call");
      }
      clientApi.addParameters({ renderer });
      const getProjectAnnotations = () => {
        const { added, removed } = executeLoadableForChanges(loadable, m);
        clientApi._loadAddedExports();
        Array.from(added.entries()).forEach(
          ([fileName, fileExports]) => clientApi.facade.addStoriesFromExports(fileName, fileExports)
        );
        Array.from(removed.entries()).forEach(
          ([fileName]) => clientApi.facade.clearFilenameExports(fileName)
        );
        return {
          render,
          ...clientApi.facade.projectAnnotations,
          renderToCanvas,
          applyDecorators: decorateStory
        };
      };
      if (!initialized) {
        preview.initialize({
          getStoryIndex: () => clientApi.getStoryIndex(),
          importFn,
          getProjectAnnotations
        });
        initialized = true;
      } else {
        getProjectAnnotations();
        onStoriesChanged();
      }
    }
  };
}

export { start };
